// "Send order to <vendor>" (F-FE-N97). One decision — how does the vendor
// get it — and one mutation: mark the order sent. WhatsApp / print run
// alongside; "Just mark sent" covers a phone call or a visit.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageCircle, Printer, Check } from 'lucide-react'
import apiClient from '@/api/client'
import type { PurchaseOrderDetail } from '@/types'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'

type ToastFn = (type: 'success' | 'error', message: string) => void

export function PoSendDialog({
  detail,
  onClose,
  showToast,
  onPrint,
}: {
  detail: PurchaseOrderDetail | null
  onClose: () => void
  showToast: ToastFn
  onPrint: (detail: PurchaseOrderDetail) => void
}) {
  const qc = useQueryClient()

  const send = useMutation({
    mutationFn: async (via: 'whatsapp' | 'print' | 'plain') => {
      if (!detail) throw new Error('No order selected')
      if (via === 'whatsapp') {
        const wa = await apiClient.sendPurchaseOrderWhatsApp(detail.id)
        const url = wa.data?.wa_url
        if (url) window.open(url, '_blank', 'noopener,noreferrer')
        else showToast('error', wa.error || 'Could not build WhatsApp link — order will still be marked sent')
      }
      if (via === 'print') onPrint(detail)
      const res = await apiClient.submitPurchaseOrder(detail.id)
      if (!res.success) throw new Error(res.error || res.message || 'Failed to mark sent')
      return via
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrder', detail?.id] })
      showToast('success', `Order marked sent to ${detail?.supplier_name ?? 'the vendor'}`)
      onClose()
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to send'),
  })

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">Send order to {detail?.supplier_name}</DialogTitle>
          <DialogDescription>
            Mark it sent, and share it however you like. Receiving stays here for when the delivery arrives.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 py-1">
          <Button className="justify-start" disabled={send.isPending} onClick={() => send.mutate('whatsapp')}>
            <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp &amp; mark sent
          </Button>
          <Button variant="outline" className="justify-start" disabled={send.isPending} onClick={() => send.mutate('print')}>
            <Printer className="mr-2 h-4 w-4" /> Print &amp; mark sent
          </Button>
          <Button variant="outline" className="justify-start" disabled={send.isPending} onClick={() => send.mutate('plain')}>
            <Check className="mr-2 h-4 w-4" /> Just mark sent
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Not yet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
